/**
 * HandoffQueueWidget — Collapsible "From ARM Dispatch" card on Requests page.
 * Shows incoming ARM handoffs that a manager must accept (or ignore) before
 * the job goes live. Each row shows routing (15-step vs standard), category,
 * customer and address, plus one-click Accept + Ignore buttons.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronRight, Radio, Check, X, RefreshCw, Shield,
} from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
};

export default function HandoffQueueWidget({ onOpenJob, onAfterChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({}); // event_id -> "accept" | "ignore"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/handoff/queue");
      setItems(data.pending || []);
      setTotal(data.pending_count || 0);
    } catch (err) {
      console.error("handoff queue failed", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // gentle poll so managers see new dispatches
    return () => clearInterval(t);
  }, [load]);

  const doAccept = async (row) => {
    setActing((s) => ({ ...s, [row.handoff_event_id]: "accept" }));
    try {
      await API.post(`/handoff/${row.handoff_event_id}/accept`);
      toast.success("Handoff accepted — job promoted to Pending");
      await load();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accept failed");
    } finally {
      setActing((s) => { const c = { ...s }; delete c[row.handoff_event_id]; return c; });
    }
  };

  const doIgnore = async (row) => {
    const reason = window.prompt("Reason for ignoring this handoff (optional)?", "") ?? undefined;
    if (reason === undefined) return; // cancelled
    setActing((s) => ({ ...s, [row.handoff_event_id]: "ignore" }));
    try {
      await API.post(`/handoff/${row.handoff_event_id}/ignore`, { reason });
      toast.success("Handoff dismissed");
      await load();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ignore failed");
    } finally {
      setActing((s) => { const c = { ...s }; delete c[row.handoff_event_id]; return c; });
    }
  };

  if (total === 0 && !loading) return null;

  return (
    <Card className="border border-sky-300 bg-sky-50/40" data-testid="handoff-queue-widget">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center justify-between gap-2 p-3 hover:bg-sky-50 rounded-t-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400"
        data-testid="handoff-queue-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-sky-700" /> : <ChevronRight className="w-4 h-4 text-sky-700" />}
          <Radio className="w-4 h-4 text-sky-700" />
          <span className="text-sm font-semibold text-sky-900">
            From ARM Dispatch — Awaiting Acceptance
          </span>
          <span className="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-sky-600 text-white text-[11px] font-bold"
                data-testid="handoff-queue-count">
            {total}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); load(); }}
          className="h-7 w-7 p-0 hover:bg-sky-100"
          data-testid="handoff-queue-refresh"
          aria-label="Refresh handoff queue"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-sky-700 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {open && (
        <div className="border-t border-sky-200 divide-y divide-sky-100">
          {loading && items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-sky-700">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-sky-700">No ARM handoffs awaiting review.</div>
          ) : items.map((it) => (
            <div key={it.handoff_event_id}
                 className="px-3 py-2.5 hover:bg-sky-100/60 transition-colors flex flex-col sm:flex-row sm:items-center gap-2"
                 data-testid={`handoff-row-${it.handoff_event_id}`}>
              <button
                type="button"
                onClick={() => onOpenJob?.(it.id, "overview")}
                className="flex-1 min-w-0 text-left"
                data-testid={`handoff-open-${it.handoff_event_id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-900 truncate">
                    {it.customer_name || "ARM Customer"}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-600 text-white">
                    ARM
                  </span>
                  {it.handoff_category && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                      {it.handoff_category}
                    </span>
                  )}
                  {it.handoff_routing === "inspection_15_step" ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                      <Shield className="w-2.5 h-2.5" /> 15-Step
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Standard
                    </span>
                  )}
                  {it.urgency_level && (
                    <span className="text-[10px] uppercase text-orange-700 font-bold">
                      {it.urgency_level}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-400">{it.request_code}</span>
                </div>
                <div className="text-[11px] text-zinc-600 truncate">
                  {it.property_address_line1}{it.property_city ? `, ${it.property_city}` : ""}{it.property_state ? `, ${it.property_state}` : ""}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {it.problem_description || "No description provided"} · Received {fmtTime(it.handoff_received_at)}
                </div>
              </button>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  size="sm"
                  onClick={() => doAccept(it)}
                  disabled={!!acting[it.handoff_event_id]}
                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  data-testid={`handoff-accept-${it.handoff_event_id}`}
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  {acting[it.handoff_event_id] === "accept" ? "…" : "Accept"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doIgnore(it)}
                  disabled={!!acting[it.handoff_event_id]}
                  className="h-8 text-xs border-zinc-300"
                  data-testid={`handoff-ignore-${it.handoff_event_id}`}
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Ignore
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
